import React, { useState, useEffect } from 'react';
import { useParams, Redirect, useLocation } from 'react-router-dom';

import GenericLoader from '@joybird/joystagram-components/dist/esm/GenericLoader';
import ErrorFallbackPage from '@joybird/joystagram-components/dist/esm/ErrorFallbackPage';
import ConsumerHeader from '@joybird/joystagram-components/dist/esm/ConsumerHeader';
import { isClient } from '../../app/globals';
import routeService from './index';
import useUserData from '@joybird/joystagram-components/dist/esm/useUserData';
import useLogoutClient from '@joybird/joystagram-components/dist/esm/useLogoutClient';

const getInitialState = routeKey => {
  const route = routeService.routes.find(({ route }) => route === routeKey);
  if (route && !route.variants) {
    return false;
  }
  return true;
};

const LazyRoute = ({
  routeKey,
  serverQueryParams,
  childProps,
  serverRouteParams,
  hasDynamicVariants = false,
  routeDynamicVariantKeys = [],
}) => {
  const params = useParams();
  const location = useLocation();
  const [isRouteDataAvailable, setRouteDataAvailable] = useState(
    getInitialState(routeKey)
  );

  // Authentication check for customer routes
  const { isLoggedIn, loading } = useUserData();
  const logoutClient = useLogoutClient();

  // Protected route prefixes that require authentication
  const PROTECTED_ROUTE_PREFIXES = ['customer', 'quote'];

  // Routes that should be excluded from protection (even if they match protected prefixes)
  const EXCLUDED_ROUTES = [
    'customer-photos',
    'customer/reset-password/customer/:reset_token',
  ];

  const isProtectedRoute = routeKey => {
    // First check if the route is explicitly excluded
    if (EXCLUDED_ROUTES.includes(routeKey)) {
      return false;
    }

    // Then check if it matches any protected prefix
    return PROTECTED_ROUTE_PREFIXES.some(prefix => routeKey.startsWith(prefix));
  };

  useEffect(() => {
    (async () => {
      if (!isRouteDataAvailable) {
        await routeService.fetchRoutesData();
        setRouteDataAvailable(true);
      }
    })();
  }, [isRouteDataAvailable]);

  // Check authentication for protected routes
  if (isRouteDataAvailable && isProtectedRoute(routeKey)) {
    if (loading) {
      return (
        <ErrorFallbackPage>
          <ConsumerHeader />
          <GenericLoader />
        </ErrorFallbackPage>
      );
    }

    if (!isLoggedIn) {
      logoutClient();
      const { search, pathname } = location;
      const redirect = `redirect=${pathname}`;
      const redirectTo = search ? `${search}&${redirect}` : `?${redirect}`;
      return <Redirect to={`/sign-in/${redirectTo}`} />;
    }
  }

  if (isRouteDataAvailable) {
    const finalRouteParams = isClient() ? params : serverRouteParams;
    const Component = routeService.getComponentForRoute(
      routeKey,
      serverQueryParams,
      finalRouteParams,
      hasDynamicVariants,
      routeDynamicVariantKeys
    );
    return <Component {...childProps} />;
  }

  return (
    <ErrorFallbackPage>
      <ConsumerHeader />
      <GenericLoader />
    </ErrorFallbackPage>
  );
};

export default LazyRoute;
